import { get } from 'lodash';
import React from 'react';
import {
  Button,
  Form,
  RadioGroup,
  RadioItem,
  SizeFittingTable,
} from 'src/components/Antd';
import Tooltip, {
  NoWrapContainer,
} from 'src/components/GlobalTooltip/GlobalTooltip';
import ProductSelection from 'src/constrains/ProductsSelection/ProductsSelection';
import { useI18n } from 'src/hooks/useRouterChange';
import Actions from 'src/pages/SalesCannelVersion/SalesChannelSettings/SalesChannelCheckout/Components/TableActions';
import { i18nGet } from 'src/tools/tools';
import { EnumTablePosition } from 'src/types/common/common';
import css from './css.module.less';

const Item = Form.Item;

export enum EInclude {
  INCLUDE_ANY_SELECTED = 'INCLUDE_ANY_SELECTED',
  INCLUDE_ALL_SELECTED = 'INCLUDE_ALL_SELECTED',
  INCLUDE_ANY = 'INCLUDE_ANY',
  EXCLUDE_ALL_SELECTED = 'EXCLUDE_ALL_SELECTED',
  EXCLUDE_ANY_SELECTED = 'EXCLUDE_ANY_SELECTED',
}
const hasTable = [
  EInclude.INCLUDE_ANY_SELECTED,
  EInclude.INCLUDE_ALL_SELECTED,
  EInclude.EXCLUDE_ALL_SELECTED,
  EInclude.EXCLUDE_ANY_SELECTED,
];
const CommonProduct = (props) => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const {
    noStyle,
    radioName,
    listName,
    onClear,
    id, // form
    hasExclude,
    disabled,
  } = props;

  return (
    <React.Fragment>
      <Item name={radioName} noStyle={noStyle}>
        <RadioGroup className={css.groupContainer} disabled={disabled}>
          <RadioItem className={css.item} value={EInclude.INCLUDE_ANY}>
            {getI18n(
              'general.advancedFilters.commonProductSelection.includeProduct',
            )}
          </RadioItem>
          <RadioItem className={css.item} value={EInclude.INCLUDE_ANY_SELECTED}>
            {getI18n(
              'general.advancedFilters.commonRadioMulSelect.includeAnySelect',
            )}
          </RadioItem>
          <RadioItem className={css.item} value={EInclude.INCLUDE_ALL_SELECTED}>
            {getI18n(
              'general.advancedFilters.commonRadioMulSelect.includeAllSelect',
            )}
          </RadioItem>
          {hasExclude && (
            <React.Fragment>
              <RadioItem
                className={css.item}
                value={EInclude.EXCLUDE_ANY_SELECTED}>
                {getI18n(
                  'general.advancedFilters.commonRadioMulSelect.excludeAnySelect',
                )}
              </RadioItem>
              <RadioItem
                className={css.item}
                value={EInclude.EXCLUDE_ALL_SELECTED}>
                {getI18n(
                  'general.advancedFilters.commonRadioMulSelect.excludeAllSelect',
                )}
              </RadioItem>
            </React.Fragment>
          )}
        </RadioGroup>
      </Item>
      <Item
        shouldUpdate={(p, c) => {
          const pT = get(p, radioName);
          const cT = get(c, radioName);
          return pT !== cT;
        }}>
        {({ getFieldValue }) => {
          const include = getFieldValue(radioName);

          if (hasTable.includes(include)) {
            return (
              <div className={css.table}>
                <Item
                  name={listName}
                  rules={[
                    {
                      validator: async (x, value) => {
                        if ((value || []).length < 1) {
                          return Promise.reject(
                            new Error(
                              getI18n(
                                'general.advancedFilters.commonStoreProductsWithTable.selectProduct',
                              ),
                            ),
                          );
                        }
                      },
                    },
                  ]}>
                  <ItemTable
                    tableId={id}
                    onClear={onClear}
                    disabled={disabled}
                  />
                </Item>
              </div>
            );
          }
        }}
      </Item>
    </React.Fragment>
  );
};
export default CommonProduct;

const ItemTable = (props) => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const { onChange, value, tableId, onClear: _onClear, id, disabled } = props;
  const [list, setList] = React.useState(value || []);
  React.useEffect(() => {
    if (list != value && value) {
      setList(value || []);
    }
  }, [value]);
  const hasData = list.length > 0;
  const [visible, setVisible] = React.useState(false);
  const onCancel = () => {
    setVisible(false);
  };
  const onAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setVisible(true);
  };
  const onOk = (products) => {
    setList(products);
    onChange(products);
  };

  const onClear = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (_onClear) {
      _onClear();
    }
    setList([]);
    onChange([]);
  };

  const col = () => {
    return [
      {
        minWidth: 320,
        title: getI18n('general.sKU'),
        key: 'Sku',
        default: true,
        childGenerate: (a, record, index) => {
          const sku = get(record, 'sku');
          return (
            <NoWrapContainer>
              <Tooltip title={sku}>{sku}</Tooltip>
            </NoWrapContainer>
          );
        },
      },
      {
        minWidth: 500,
        title: getI18n('general.productName'),
        key: 'name',
        default: true,
        childGenerate: (a, record, index) => {
          const name = get(record, 'name');
          return (
            <NoWrapContainer>
              <Tooltip title={name}>{name}</Tooltip>
            </NoWrapContainer>
          );
        },
      },
      {
        titleGenerate: () =>
          getI18n(
            'general.advancedFilters.commonStoreProductsWithTable.actions',
          ),
        position: EnumTablePosition.mid,
        key: 'Actions',
        default: true,
        childGenerate: (url, record, index) => {
          return (
            <Actions
              disabled={disabled}
              onDelete={() => {
                const _list = [...list];
                _list.splice(index, 1);
                onOk(_list);
                //  remove(index);
              }}
            />
          );
        },
        width: 190,
      },
    ];
  };
  return (
    <React.Fragment>
      {visible && (
        <ProductSelection
          visible={visible}
          onCancel={onCancel}
          onOk={onOk}
          selectedItems={list || []}
          extraProps={{ keepData: true }}
        />
      )}
      {hasData ? (
        <React.Fragment>
          <SizeFittingTable
            rowKey='uuid'
            pagination={false}
            dataSource={list || []}
            id={`${tableId}CommonFilterProductTable`}
            columns={col()}
          />
        </React.Fragment>
      ) : null}{' '}
      <div className={css.tableBtnGroup} id={id}>
        <Button onClick={onAdd} className={css.add} disabled={disabled}>
          {getI18n('general.selectProduct')}
        </Button>
        <Button onClick={onClear} className={css.clear} disabled={disabled}>
          {getI18n(
            'general.advancedFilters.commonStoreProductsWithTable.clear',
          )}
        </Button>
      </div>
    </React.Fragment>
  );
};
